import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { features } from '../config';

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'kgp-theme-mode';

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  darkModeAvailable: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const darkModeAvailable = __THEME_HAS_DARK__ && features.darkMode;

const getStoredMode = (): ThemeMode => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
};

const systemPrefersDark = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveTheme = (mode: ThemeMode): 'light' | 'dark' =>
  mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode;

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    darkModeAvailable ? resolveTheme(getStoredMode()) : 'light');

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — persistence unavailable */
    }
    setResolvedTheme(darkModeAvailable ? resolveTheme(next) : 'light');
  };

  // Apply .dark + theme-color meta whenever the resolved theme changes
  useEffect(() => {
    const isDark = darkModeAvailable && resolvedTheme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? __THEME_COLOR_DARK__ : __THEME_COLOR_LIGHT__);
  }, [resolvedTheme]);

  // Follow OS changes while in system mode
  useEffect(() => {
    if (!darkModeAvailable || mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(resolveTheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const value = useMemo(
    () => ({ mode, resolvedTheme, setMode, darkModeAvailable }),
    [mode, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
